export type ObservableMap<K, V> = Map<K, V> & {
  onUpdate: (key: string, callback: () => void) => () => void;
};

function createObservableCallbacks() {
  const keyUpdateHandlers = new Map<string, Set<() => void>>();

  return {
    onUpdate(key: string, handler: () => void): () => void {
      const handlers = keyUpdateHandlers.get(key) ?? new Set<() => void>();
      handlers.add(handler);
      keyUpdateHandlers.set(key, handlers);
      return () => {
        handlers.delete(handler);
        if (handlers.size === 0) keyUpdateHandlers.delete(key);
      };
    },
    notifyUpdate(key: string): void {
      for (const handler of keyUpdateHandlers.get(key) ?? []) handler();
    },
    notifyClear(): void {
      for (const handlers of keyUpdateHandlers.values()) {
        for (const handler of handlers) handler();
      }
    },
  };
}

//FIXME: allow different type for different properties
export function memoryMap<T>(
  backend: Map<string, T> = new Map<string, T>(),
): ObservableMap<string, T> {
  const callbacks = createObservableCallbacks();
  const theMemoryMap: ObservableMap<string, T> = {
    onUpdate: callbacks.onUpdate,
    delete: (key: string) => {
      const result = backend.delete(key);
      callbacks.notifyUpdate(key);
      return result;
    },
    set: (key: string, value: T) => {
      backend.set(key, value);
      callbacks.notifyUpdate(key);
      return theMemoryMap;
    },
    clear: () => {
      backend.clear();
      callbacks.notifyClear();
    },
    entries: backend.entries.bind(backend),
    forEach: backend.forEach.bind(backend),
    get: backend.get.bind(backend),
    has: backend.has.bind(backend),
    keys: backend.keys.bind(backend),
    get size() {
      return backend.size;
    },
    values: backend.values.bind(backend),
    [Symbol.iterator]: backend[Symbol.iterator].bind(backend),
    [Symbol.toStringTag]: "theMemoryMap",
  };
  return theMemoryMap;
}

type StorageProvider = () => Storage | undefined;

function defaultStorageProvider(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  return window.localStorage;
}

/**
 * An observable local-storage map with an in-memory fallback.
 *
 * Browser storage can be unavailable or start throwing at any time, for
 * example in privacy-restricted contexts or after exceeding its quota.  The
 * fallback keeps the UI operational for the remainder of the page session.
 */
export function localStorageMap(
  storageProvider: StorageProvider = defaultStorageProvider,
): ObservableMap<string, string> {
  const callbacks = createObservableCallbacks();
  const fallback = new Map<string, string>();
  let disabledBackend: Storage | undefined;

  function getBackend(): Storage | undefined {
    try {
      const backend = storageProvider();
      return backend === disabledBackend ? undefined : backend;
    } catch {
      return undefined;
    }
  }

  function disableBackend(backend: Storage): void {
    disabledBackend = backend;
  }

  function get(key: string): string | undefined {
    const backend = getBackend();
    if (!backend) return fallback.get(key);
    try {
      const value = backend.getItem(key);
      if (value === null) {
        fallback.delete(key);
        return undefined;
      }
      fallback.set(key, value);
      return value;
    } catch {
      disableBackend(backend);
      return fallback.get(key);
    }
  }

  function snapshot(): Map<string, string> {
    const backend = getBackend();
    if (!backend) return new Map(fallback);
    try {
      const content = new Map<string, string>();
      for (let index = 0; index < backend.length; index++) {
        const key = backend.key(index);
        if (key === null) continue;
        const value = backend.getItem(key);
        if (value !== null) content.set(key, value);
      }
      fallback.clear();
      for (const [key, value] of content) fallback.set(key, value);
      return content;
    } catch {
      disableBackend(backend);
      return new Map(fallback);
    }
  }

  function storageEventMatches(event: StorageEvent): boolean {
    if (event.storageArea === null) return true;
    return event.storageArea === getBackend();
  }

  const theLocalStorageMap: ObservableMap<string, string> = {
    onUpdate: (key, handler) => {
      function handleStorageEvent(ev: StorageEvent) {
        if (storageEventMatches(ev) && (ev.key === null || ev.key === key)) {
          handler();
        }
      }
      const eventWindow = typeof window === "undefined" ? undefined : window;
      const unsubscribe = callbacks.onUpdate(key, handler);
      eventWindow?.addEventListener("storage", handleStorageEvent);
      return () => {
        eventWindow?.removeEventListener("storage", handleStorageEvent);
        unsubscribe();
      };
    },
    delete: (key: string) => {
      const exists = get(key) !== undefined;
      fallback.delete(key);
      const backend = getBackend();
      if (backend) {
        try {
          backend.removeItem(key);
        } catch {
          disableBackend(backend);
        }
      }
      callbacks.notifyUpdate(key);
      return exists;
    },
    set: (key: string, v: string) => {
      fallback.set(key, v);
      const backend = getBackend();
      if (backend) {
        try {
          backend.setItem(key, v);
        } catch {
          disableBackend(backend);
        }
      }
      callbacks.notifyUpdate(key);
      return theLocalStorageMap;
    },
    clear: () => {
      fallback.clear();
      const backend = getBackend();
      if (backend) {
        try {
          backend.clear();
        } catch {
          disableBackend(backend);
        }
      }
      callbacks.notifyClear();
    },
    entries: () => snapshot().entries(),
    forEach: (cb, thisArg) => {
      snapshot().forEach((value, key) =>
        cb.call(thisArg, value, key, theLocalStorageMap),
      );
    },
    get,
    has: (key: string) => get(key) !== undefined,
    keys: () => snapshot().keys(),
    get size() {
      return snapshot().size;
    },
    values: () => snapshot().values(),
    [Symbol.iterator]: function (): IterableIterator<[string, string]> {
      return theLocalStorageMap.entries();
    },
    [Symbol.toStringTag]: "theLocalStorageMap",
  };
  return theLocalStorageMap;
}
